import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessagesSquare, Target, Rocket, Headphones, ShieldCheck, Handshake, Sparkles, Quote } from 'lucide-react';
import UnifiedHistory from './UnifiedHistory';
import TeamSection from './TeamSection';
import Reveal from './Reveal';
import Magnetic from './Magnetic';
import { MnArrowUpRightIcon } from './MnIcons';

const WA_NUMBER = '593987119183';
const waLink = (msg: string) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

const AboutUs: React.FC = () => {
  const { t } = useTranslation();

  // 6 de los 8 diferenciadores redactados en las traducciones — se dejó fuera
  // 'objective_2' por ser casi un duplicado de 'diff_1' (mismo mensaje: "100%
  // enfocados en dental, no un CRM genérico"), para no repetir el punto dos veces.
  // Cada tarjeta lleva su propio color de acento (antes las 6 eran teal
  // monocromo) — es lo que le da variedad tipo "bento" a un grid de solo
  // texto, junto con el ícono grande de fondo (ver .why-card-icon-bg).
  const whyUs = [
    {
      icon: <Target className="why-icon-svg" />,
      bgIcon: <Target className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.diff_1_title'),
      desc: t('about_page.diff_1_desc'),
      accent: '#00e5d1',
    },
    {
      icon: <MessagesSquare className="why-icon-svg" />,
      bgIcon: <MessagesSquare className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.objective_1_title'),
      desc: t('about_page.objective_1_desc'),
      accent: '#fbbf24',
    },
    {
      icon: <Rocket className="why-icon-svg" />,
      bgIcon: <Rocket className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.objective_3_title'),
      desc: t('about_page.objective_3_desc'),
      accent: '#8b5cf6',
    },
    {
      icon: <Headphones className="why-icon-svg" />,
      bgIcon: <Headphones className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.diff_3_title'),
      desc: t('about_page.diff_3_desc'),
      accent: '#3b82f6',
    },
    {
      icon: <ShieldCheck className="why-icon-svg" />,
      bgIcon: <ShieldCheck className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.diff_2_title'),
      desc: t('about_page.diff_2_desc'),
      accent: '#fb7185',
    },
    {
      icon: <Handshake className="why-icon-svg" />,
      bgIcon: <Handshake className="why-card-icon-bg" strokeWidth={1} />,
      title: t('about_page.diff_4_title'),
      desc: t('about_page.diff_4_desc'),
      accent: '#34d399',
    },
  ];

  const caseStats = [
    { value: t('social_proof.stat1_value', '1.032'), label: t('social_proof.stat1_label', 'conversaciones IA este mes') },
    { value: t('social_proof.stat2_value', '88%'), label: t('social_proof.stat2_label', 'automatización') },
    { value: t('social_proof.stat3_value', '137,6h'), label: t('social_proof.stat3_label', 'ahorradas') },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="about-container"
    >
      {/* Section 1: Nuestra Historia Unificada */}
      <div id="historia">
        <UnifiedHistory />
      </div>

      {/* Section 2: Equipo — caras reales primero, generan confianza más rápido */}
      <TeamSection />

      {/* Section 3: Prueba social — antes era solo la tarjeta flotando sola
          sobre negro plano; se le suma un glow ambiente detrás (mismo
          recurso que el resto de secciones, para que no se sienta "seca")
          y una fila de métricas reales debajo (conversaciones IA,
          automatización, horas ahorradas — datos del último mes, ver
          social_proof.stat*_value) — nada inventado, solo se les da su
          propio espacio visual en vez de quedar enterradas en un párrafo. */}
      <section id="prueba-social" className="about-section social-proof-section">
        {/* La línea que se notaba arriba no era el glow (que es simétrico) —
            es el borde real entre TeamSection (arriba) y esta sección. Este
            fundido tapa esa costura igual que se hizo con el hero del blog:
            mismo color de fondo (var(--bg-dark)) yendo de opaco a
            transparente, en vez de dejar el corte duro entre secciones. */}
        <div aria-hidden className="social-proof-top-fade" />
        <div aria-hidden className="social-proof-glow" />
        <div className="container">
          <Reveal>
            <div className="social-proof-card">
              <span className="mn-tag social-proof-tag"><Sparkles size={12} strokeWidth={2.5} />{t('social_proof.eyebrow')}</span>
              <h2 className="social-proof-title">{t('social_proof.title')}</h2>
              <div className="social-proof-client">
                <span className="social-proof-dot" />
                <div style={{ flex: 1 }}>
                  <div className="social-proof-name">{t('social_proof.client_name')}</div>
                  <p className="social-proof-desc">{t('social_proof.client_desc')}</p>
                  <div className="social-proof-stats">
                    {caseStats.map((s, i) => (
                      <div key={i} className="social-proof-stat">
                        <div className="social-proof-stat-value">{s.value}</div>
                        <div className="social-proof-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Cita real del dueño/gerente general, autorizada por él para
                      publicarse con nombre y cargo — ver social_proof.quote*.
                      Sin estrellas ni foto de stock: avatar con iniciales y el
                      gradiente de marca, igual criterio que el Home. Frase
                      destacada en teal sólido, no gradient-clip — a tamaño de
                      texto de cuerpo el tramo morado del degradado pierde
                      contraste legible en fondo oscuro. */}
                  <div className="social-proof-quote">
                    <Quote size={20} color="var(--accent-teal)" style={{ opacity: 0.5 }} />
                    <p className="social-proof-quote-text">
                      "{t('social_proof.quote_pre')}<span style={{ color: 'var(--accent-teal)', fontWeight: 700 }}>{t('social_proof.quote_highlight')}</span>{t('social_proof.quote_post')}"
                    </p>
                    <div className="social-proof-quote-author">
                      <span className="social-proof-avatar">GM</span>
                      <div>
                        <div className="social-proof-avatar-name">{t('social_proof.quote_name')}</div>
                        <div className="social-proof-avatar-role">{t('social_proof.quote_role')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 4: Por qué elegirnos — 4 puntos, sin relleno */}
      <section id="diferenciadores" className="about-section why-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="section-content"
          >
            <h2 className="grad-text section-title why-section-title">{t('about_page.differentiators_title')}</h2>
            <p className="section-desc">{t('about_page.differentiators_subtitle')}</p>

            <div className="why-grid">
              {whyUs.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="why-card"
                  style={{ '--why-accent': item.accent } as React.CSSProperties}
                >
                  {item.bgIcon}
                  <div className="why-icon">{item.icon}</div>
                  <h3 className="why-title">{item.title}</h3>
                  <p className="why-desc">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 5: CTA de cierre — antes era texto+botón sueltos sobre negro
          plano; ahora usa la misma tarjeta "prod-cta" (vidrio + glow
          radial + hairline superior) que ya tienen los CTA de cierre de
          Blog y Casos de Uso, para que las 3 páginas terminen igual. */}
      <section className="about-section about-cta-section">
        <div className="container">
          <motion.div
            className="prod-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div aria-hidden className="prod-cta-hairline" />
            <div aria-hidden className="prod-cta-glow prod-cta-glow-a" />
            <div aria-hidden className="prod-cta-glow prod-cta-glow-b" />

            <div className="prod-cta-content">
              <h2 className="about-cta-title">{t('about_cta.title')}</h2>
              <Magnetic>
                <a
                  className="about-cta-btn trial-btn"
                  href={waLink(t('about_cta.wa_message'))}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('about_cta.primary')}
                  <MnArrowUpRightIcon size={16} />
                </a>
              </Magnetic>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .about-container {
          background: transparent;
          position: relative;
        }

        .about-container::before {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0.035;
          mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }

        .about-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: clamp(4rem, 8vw, 7rem) 0;
          position: relative;
          z-index: 1;
        }

        .section-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        .section-title {
          font-size: clamp(2.2rem, 4.6vw, 3.4rem);
          font-weight: 800;
          margin-bottom: 1rem;
          letter-spacing: -0.03em;
        }

        /* "Por qué elegirnos" recortaba la "g" descendente de "elegirnos":
           .grad-text es display:inline-block y hereda el line-height:1.1 del
           h2 global, muy justo para el descendente de una "g" en Manrope 800
           combinado con background-clip:text — el navegador clippea el
           glifo contra la caja de línea. Con más line-height y un pelín de
           padding-bottom alcanza para que el trazo completo entre. */
        .why-section-title {
          line-height: 1.35;
          padding-bottom: 0.1em;
        }

        .section-desc {
          font-size: clamp(1.05rem, 1.8vw, 1.25rem);
          color: var(--text-secondary);
          line-height: 1.6;
          max-width: 700px;
          margin: 0 auto;
        }

        /* Bento de 3×2, todas las celdas del mismo tamaño (como la
           referencia) — la variedad no viene de romper la grilla en
           tamaños distintos (con solo texto+ícono no hay contenido real
           para llenar una celda 2x más grande sin quedar vacía), sino de
           que cada tarjeta tenga su propio color de acento y un ícono
           grande de fondo recortado en la esquina, en vez de las 6
           idénticas en monotono teal que había antes. */
        .why-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          margin-top: 3.5rem;
          width: 100%;
          text-align: left;
        }

        .why-card {
          position: relative;
          overflow: hidden;
          background: var(--bg-elevated);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: clamp(1.5rem, 2vw, 1.85rem);
          min-height: 0;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }

        .why-card:hover {
          border-color: color-mix(in srgb, var(--why-accent) 45%, var(--border-lighter));
          transform: translateY(-3px);
          box-shadow: 0 24px 48px -28px color-mix(in srgb, var(--why-accent) 55%, transparent);
        }

        /* Ícono grande, apenas insinuado, en la esquina superior derecha —
           recortado por el overflow:hidden de la tarjeta. Es lo que rompe
           la monotonía "texto plano apilado" sin necesitar una imagen real. */
        .why-card-icon-bg {
          position: absolute;
          top: -18px;
          right: -18px;
          width: 108px;
          height: 108px;
          color: var(--why-accent);
          opacity: 0.16;
          pointer-events: none;
          transition: opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease;
        }

        .why-card:hover .why-card-icon-bg {
          opacity: 0.55;
          transform: scale(1.06);
          filter: drop-shadow(0 0 14px var(--why-accent));
        }

        .why-icon {
          width: 40px;
          height: 40px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.85rem;
          background: color-mix(in srgb, var(--why-accent) 14%, transparent);
          color: var(--why-accent);
          position: relative;
          z-index: 1;
        }

        .why-icon-svg {
          width: 22px;
          height: 22px;
        }

        .why-title {
          position: relative;
          z-index: 1;
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.6rem;
          color: var(--text-primary);
          letter-spacing: -0.01em;
        }

        .why-desc {
          position: relative;
          z-index: 1;
          font-size: 0.98rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        @media (max-width: 900px) {
          .why-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 640px) {
          .why-grid {
            grid-template-columns: 1fr;
          }
          .why-card {
            min-height: 0;
          }
        }

        /* ── Prueba social ── */
        .social-proof-section {
          position: relative;
          overflow: hidden;
          padding-top: clamp(3rem, 6vw, 5rem);
          padding-bottom: clamp(3rem, 6vw, 5rem);
        }

        /* Antes la tarjeta flotaba sola sobre negro plano — este glow (mismo
           recurso que usa el resto del sitio, p.ej. el hero) le da algo de
           ambiente detrás sin competir con el contenido. */
        /* width/height más chicos que el alto real de la sección y el
           gradiente apagándose a los 45% (no 65%) — así el difuminado
           termina de desvanecerse ANTES de tocar el borde de la sección;
           si se pasa, overflow:hidden lo recorta en una línea recta arriba
           y abajo en vez de dejarlo esfumarse (la costura que se veía). */
        .social-proof-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 640px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, var(--accent-teal-dim) 0%, transparent 45%);
          filter: blur(90px);
          pointer-events: none;
          z-index: 0;
        }

        .social-proof-top-fade {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 140px;
          background: linear-gradient(180deg, var(--bg-dark) 0%, transparent 100%);
          pointer-events: none;
          z-index: 0;
        }

        .social-proof-card {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 960px;
          margin: 0 auto;
          background: var(--bg-elevated);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: clamp(2rem, 4vw, 3rem);
          text-align: left;
          box-shadow: 0 40px 80px -40px rgba(0, 0, 0, 0.5);
        }

        .social-proof-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--font-body);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--accent-teal);
          background: rgba(0, 229, 209, 0.08);
          border: 1px solid rgba(0, 229, 209, 0.25);
          padding: 0.35rem 0.75rem;
          border-radius: 9999px;
        }

        .social-proof-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin: 1.5rem 0;
          padding: 1.25rem 0;
          border-top: 1px solid var(--border-light);
          border-bottom: 1px solid var(--border-light);
        }

        .social-proof-stat-value {
          font-family: var(--font-heading);
          font-size: clamp(1.3rem, 2.6vw, 1.7rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          font-variant-numeric: tabular-nums;
          margin-bottom: 0.25rem;
        }

        .social-proof-stat-label {
          font-size: 0.76rem;
          line-height: 1.35;
          color: var(--text-secondary);
        }

        @media (max-width: 560px) {
          .social-proof-stats { grid-template-columns: 1fr 1fr 1fr; gap: 0.6rem; }
        }

        .social-proof-quote {
          margin-top: 0.25rem;
        }

        .social-proof-quote-text {
          font-size: 0.92rem;
          font-weight: 300;
          line-height: 1.55;
          text-align: justify;
          text-justify: inter-word;
          hyphens: auto;
          color: var(--text-primary);
          margin: 0.5rem 0 0.9rem;
        }

        .social-proof-quote-author {
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .social-proof-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-heading);
          font-weight: 800;
          font-size: 0.68rem;
          letter-spacing: -0.02em;
          color: #fff;
          background: linear-gradient(135deg, #00e5d1 0%, #8b5cf6 100%);
        }

        .social-proof-avatar-name {
          font-weight: 700;
          font-size: 0.82rem;
          color: var(--text-primary);
        }

        .social-proof-avatar-role {
          font-size: 0.74rem;
          color: var(--text-muted);
        }

        .social-proof-title {
          font-family: var(--font-heading);
          font-size: clamp(1.6rem, 3vw, 2.1rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          margin: 1rem 0 1.75rem;
        }

        .social-proof-client {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-light);
        }

        .social-proof-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--accent-teal);
          margin-top: 0.4rem;
          flex-shrink: 0;
          box-shadow: 0 0 0 4px rgba(0, 229, 209, 0.12);
        }

        .social-proof-name {
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 1.05rem;
          color: var(--text-primary);
          margin-bottom: 0.35rem;
        }

        .social-proof-desc {
          font-size: 0.95rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .social-proof-note {
          font-size: 0.8rem;
          font-style: italic;
          color: var(--text-secondary);
          opacity: 0.7;
          line-height: 1.5;
          margin-top: 0.75rem;
        }

        /* ── CTA de cierre — mismo "prod-cta" que Blog/Casos de Uso (vidrio +
           glow radial + hairline superior); acá se define local porque cada
           página monta/desmonta su propio <style>, así que no se puede
           reusar el de esas otras rutas. */
        .about-cta-section {
          text-align: center;
        }

        .prod-cta {
          position: relative;
          overflow: hidden;
          border-radius: 32px;
          padding: clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem);
          backdrop-filter: blur(16px);
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, rgba(8,8,8,0.6) 100%);
        }
        [data-theme='light'] .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }

        .prod-cta-hairline {
          position: absolute;
          top: 0; left: 12%; right: 12%; height: 1px;
          background: linear-gradient(90deg, transparent, var(--accent-teal), transparent);
          opacity: 0.55;
          pointer-events: none;
        }

        .prod-cta-glow {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .prod-cta-glow-a {
          top: -90px; left: 50%; transform: translateX(-50%);
          width: 360px; height: 360px;
          background: #00e5d1;
          opacity: 0.16;
          filter: blur(100px);
        }
        .prod-cta-glow-b {
          bottom: -120px; right: -40px;
          width: 320px; height: 320px;
          background: var(--accent-purple);
          opacity: 0.08;
          filter: blur(110px);
        }

        .prod-cta-content {
          position: relative;
          z-index: 1;
          max-width: 640px;
          margin: 0 auto;
        }

        .about-cta-title {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4.5vw, 3.2rem);
          font-weight: 300;
          letter-spacing: -0.03em;
          line-height: 1.12;
          color: var(--text-primary);
          margin: 0 0 1.6rem;
        }

        /* Blanco en oscuro / negro en claro — igual que el botón "Agendar
           demo" de los CTA de cierre de Blog y Casos de Uso (.trial-btn ahí
           también); se define local por la misma razón que .prod-cta. */
        .about-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--font-heading);
          font-size: 0.95rem;
          font-weight: 700;
          padding: 1rem 1.8rem;
          border-radius: 980px;
          border: none;
          text-decoration: none;
        }
        .trial-btn {
          background: #fff;
          color: #000;
          transition: background 0.2s ease;
        }
        .trial-btn:hover { background: #e8e8ed; }
        [data-theme='light'] .trial-btn { background: #000; color: #fff; }
        [data-theme='light'] .trial-btn:hover { background: #1d1d1f; }
      `}</style>
    </motion.div>
  );
};

export default AboutUs;
